// Image processing and compression utilities for InstaWorthy

/**
 * Compress an image file to optimize for upload
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels  
 * @param {number} quality - JPEG quality (0.1 to 1.0)
 * @returns {Promise<Blob>} - Compressed image blob
 */
export const compressImage = (file, maxWidth = 1024, maxHeight = 1024, quality = 0.8) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Calculate new dimensions while maintaining aspect ratio
            let { width, height } = img;
            
            if (width > height) {
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }
            }

            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;

            // Draw and compress
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob(resolve, 'image/jpeg', quality);
        };

        img.src = URL.createObjectURL(file);
    });
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @param {number} maxSize - Maximum file size in bytes
 * @returns {Object} - Validation result
 */
export const validateImage = (file, maxSize = 10 * 1024 * 1024) => {
    const errors = [];
    
    // Check file type
    if (!file.type.startsWith('image/')) {
        errors.push('File must be an image');
    }
    
    // Check supported formats
    const supportedTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
    if (!supportedTypes.includes(file.type)) {
        errors.push('Supported formats: JPEG, PNG, HEIC, WebP');
    }
    
    // Check file size
    if (file.size > maxSize) {
        errors.push(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
    }
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Generate a unique filename for upload
 * @param {string} userId - User ID
 * @param {string} originalName - Original filename
 * @returns {string} - Unique filename
 */
export const generateFileName = (userId, originalName) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop().toLowerCase();
    
    return `users/${userId}/selfies/${timestamp}_${random}.${extension}`;
};

/**
 * Create a preview URL for an image file
 * @param {File} file - The image file
 * @returns {string} - Preview URL
 */
export const createPreviewUrl = (file) => {
    return URL.createObjectURL(file);
};

/**
 * Clean up preview URLs to prevent memory leaks
 * @param {string} url - Preview URL to revoke
 */
export const revokePreviewUrl = (url) => {
    URL.revokeObjectURL(url);
};